import styled from '@emotion/styled';
import React from 'react';
import type { LineHeight, Variants } from 'types/emotion.d.ts';
import { palette } from 'styles/theme/palette';

export type TextColorKey = keyof typeof palette.text;
export type TextColor = TextColorKey | 'inherit';

const isTextColorKey = (color: string): color is TextColorKey => {
  return color in palette.text;
};

export type TextProps = {
  variant?: Variants;
  weight?: 'regular' | 'medium' | 'semiBold' | 'bold';
  underlined?: boolean;
  align?: 'left' | 'center' | 'right';
  noWrap?: boolean;
  fontFamily?: 'Open Sans' | 'Gazpacho';
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  color?: TextColor;
  children?: React.ReactNode;
  lineHeight?: keyof LineHeight;
};
type TextVariantType = {
  [key: string]: {
    fontWeight: number;
  };
};

export const Text = styled.span<TextProps>`
  color: ${({ theme, color }) => {
    if (!color) {
      return theme.palette.text.primary;
    }
    if (isTextColorKey(color)) {
      return theme.palette.text[color];
    }
    return color;
  }};
  font-weight: ${({ theme, weight }) => theme.typography.weight?.[weight ?? 'regular']};
  font-size: ${({ theme, variant }) => theme.typography[variant || 'bodyMd'].fontSize};
  ${({ fontFamily }) => (fontFamily ? `font-family: ${fontFamily};` : '')}
  ${({ underlined }) => (underlined ? 'text-decoration: underline;' : '')}
  ${({ lineHeight, theme, variant }) => {
    const lineH =
      (lineHeight && theme.typography.lineHeight[lineHeight]) ??
      theme.typography[variant || 'bodyMd'].lineHeight;
    return lineH ? `line-height:${lineH};` : '';
  }}
  ${({ align }) => (align ? `text-align: ${align};` : '')}
  ${({ top }) => (top ? `margin-top: ${top}px;` : '')}
  ${({ bottom }) => (bottom ? `margin-bottom: ${bottom}px;` : '')}
  ${({ left }) => (left ? `margin-left: ${left}px;` : '')}
  ${({ right }) => (right ? `margin-right: ${right}px;` : '')}
  ${({ noWrap }) =>
    noWrap ? 'white-space: nowrap; overflow: hidden; text-overflow: ellipsis;' : ''}
`;

export const textVariant: TextVariantType = {
  regular: {
    fontWeight: 400,
  },
  medium: {
    fontWeight: 500,
  },
  semiBold: {
    fontWeight: 600,
  },
  bold: {
    fontWeight: 700,
  },
};
